<template>
  <div class="changelog-entry-content">
    <!-- Left: Album Info (Cover + Artist/Title) -->
    <div class="album-info-section">
      <div class="album-cover-container">
        <div v-if="!entry.cover_url || imageError" class="album-cover-placeholder">
          <Music :size="40" class="placeholder-icon" />
        </div>
        <div v-else class="album-cover-wrapper">
          <img
            :src="entry.cover_url"
            :alt="`${entry.title} by ${entry.artist}`"
            class="album-cover"
            :class="{ 'image-loaded': imageLoaded }"
            @load="imageLoaded = true"
            @error="imageLoadError"
          />
          <div v-if="!imageLoaded" class="album-cover-loading">
            <div class="loading-spinner"></div>
          </div>
        </div>
      </div>

      <button
        class="album-text-info"
        @click="openAlbumDetails"
        :title="`View details for ${entry.title} by ${entry.artist}`"
      >
        <div class="artist-name">{{ entry.artist }}</div>
        <div class="album-title">{{ entry.title }}</div>
      </button>
    </div>

    <!-- Right: Change Details -->
    <div class="change-details-section">
      <div class="change-description">
        <div v-if="entry.change_type === 'Overall Rating Change'" class="change-text">
          <div class="change-main-text">Overall rating changed from</div>
          <div class="rating-values">
            <span class="old-rating" :class="getRatingClass(entry.old_rating)">
              <Skull v-if="entry.old_rating === 'Tiering Herrie'" :size="12" class="skull-icon" />
              {{ entry.old_rating }}
            </span>
            <ArrowRight :size="16" class="arrow-icon" />
            <span class="new-rating" :class="getRatingClass(entry.new_rating)">
              <Skull v-if="entry.new_rating === 'Tiering Herrie'" :size="12" class="skull-icon" />
              {{ entry.new_rating }}
            </span>
          </div>
        </div>

        <div v-else class="change-text">
          <div class="change-main-text">
            <span class="reviewer-info">{{ entry.reviewer }}'s</span> rating changed from
          </div>
          <div class="rating-values">
            <span class="old-rating" :class="getRatingClass(entry.old_rating)">
              <Skull v-if="entry.old_rating === 'Tiering Herrie'" :size="12" class="skull-icon" />
              {{ entry.old_rating }}
            </span>
            <ArrowRight :size="16" class="arrow-icon" />
            <span class="new-rating" :class="getRatingClass(entry.new_rating)">
              <Skull v-if="entry.new_rating === 'Tiering Herrie'" :size="12" class="skull-icon" />
              {{ entry.new_rating }}
            </span>
          </div>
        </div>
      </div>

      <div class="change-metadata">
        <Clock :size="14" class="metadata-icon" />
        <time :datetime="entry.change_date" :title="formatAbsoluteDate(entry.change_date)">
          {{ formatRelativeTime(entry.change_date) }}
        </time>
      </div>
    </div>
  </div>
  
  <!-- Album Details Modal - Teleported to body -->
  <Teleport to="body">
    <AlbumDetailsModal
      v-if="showAlbumModal && albumForModal"
      :album="albumForModal"
      :veto="albumForModal.veto || false"
      :veto_by="albumForModal.veto_by || null"
      @close="closeAlbumModal"
      @albumDeleted="handleAlbumDeleted"
    />
  </Teleport>
</template>

<script>
import { computed, ref } from 'vue'
import AlbumDetailsModal from '@/components/modals/AlbumDetailsModal.vue'
import apiService from '@/apiService'
import {
  ArrowRight, // eslint-disable-line no-unused-vars
  Clock, // eslint-disable-line no-unused-vars
  Music,
  Skull // eslint-disable-line no-unused-vars
} from 'lucide-vue-next'

export default {
  name: 'ChangelogEntry',
  components: {
    AlbumDetailsModal,
    ArrowRight,
    Clock,
    Music,
    Skull
  },
  props: {
    entry: {
      type: Object,
      required: true,
      validator: (entry) => {
        return entry.id && entry.change_type && entry.change_date
      }
    }
  },
  setup(props) {
    // Reactive state
    const showAlbumModal = ref(false)
    const albumForModal = ref(null)
    const loadingAlbum = ref(false)
    const imageLoaded = ref(false)
    const imageError = ref(false)
    
    // Computed properties
    const showRatingDelta = computed(() => {
      return props.entry.old_rating && props.entry.new_rating
    })

    // Methods
    const getRatingClass = (rating) => {
      if (!rating) return 'rating-not-rated'
      
      // Handle tier letters directly
      if (rating === 'S') return 'rating-s'
      if (rating === 'A') return 'rating-a'
      if (rating === 'B') return 'rating-b'
      if (rating === 'C') return 'rating-c'
      if (rating === 'D') return 'rating-d'
      if (rating === 'E') return 'rating-e'
      
      // Handle "Now Listening To" as a special tier
      if (rating === 'Now Listening To') return 'rating-now-listening'
      
      // Handle special "Tiering Herrie" tier (worst tier)
      if (rating === 'Tiering Herrie') {
        return 'rating-tiering-herrie'
      }
      
      // Handle unrated/undefined cases
      if (rating === '?' || rating === 'not rated yet' || rating === 'Not Rated Yet') {
        return 'rating-not-rated'
      }
      
      // Handle numeric ratings as fallback
      const numRating = parseFloat(rating)
      if (!isNaN(numRating)) {
        if (numRating >= 90) return 'rating-s'
        if (numRating >= 80) return 'rating-a'
        if (numRating >= 70) return 'rating-b'
        if (numRating >= 60) return 'rating-c'
        if (numRating >= 50) return 'rating-d'
        return 'rating-e'
      }
      
      // Default fallback for any unrecognized rating
      return 'rating-not-rated'
    }

    const getRatingDelta = () => {
      if (!props.entry.old_rating || !props.entry.new_rating) return ''
      
      const oldRating = parseFloat(props.entry.old_rating)
      const newRating = parseFloat(props.entry.new_rating)
      const delta = newRating - oldRating
      
      if (delta > 0) return `+${delta.toFixed(1)}`
      if (delta < 0) return delta.toFixed(1)
      return '0.0'
    }

    const getRatingDeltaClass = () => {
      if (!props.entry.old_rating || !props.entry.new_rating) return ''
      
      const oldRating = parseFloat(props.entry.old_rating)
      const newRating = parseFloat(props.entry.new_rating)
      const delta = newRating - oldRating
      
      if (delta > 0) return 'delta-positive'
      if (delta < 0) return 'delta-negative'
      return 'delta-neutral'
    }

    const formatRelativeTime = (dateString) => {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      const diffWeeks = Math.floor(diffDays / 7)
      const diffMonths = Math.floor(diffDays / 30)

      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
      if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
      
      return formatAbsoluteDate(dateString)
    }

    const formatAbsoluteDate = (dateString) => {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      }
      return new Date(dateString).toLocaleDateString(undefined, options)
    }

    const openAlbumDetails = async () => {
      console.log('Album click - entry data:', props.entry)
      
      const albumId = props.entry.album_id || props.entry.id
      if (!albumId) {
        console.error('Cannot open album details - no identifier found')
        return
      }
      
      if (loadingAlbum.value) return
      
      try {
        loadingAlbum.value = true
        console.log('Fetching album data for ID:', albumId)
        
        const albumData = await apiService.getAlbum(albumId)
        console.log('Successfully loaded album data:', albumData)
        
        albumForModal.value = albumData
        showAlbumModal.value = true
      } catch (error) {
        console.error('Failed to fetch album data:', error)
        alert('Failed to load album details. Please try again.')
      } finally {
        loadingAlbum.value = false
      }
    }

    const closeAlbumModal = () => {
      showAlbumModal.value = false
      albumForModal.value = null
    }

    const handleAlbumDeleted = (albumId) => {
      console.log('Album deleted:', albumId)
      closeAlbumModal()
      // Could emit an event to parent to refresh changelog if needed
    }

    const imageLoadError = () => {
      imageError.value = true
      imageLoaded.value = false
    }

    return {
      showAlbumModal,
      albumForModal,
      loadingAlbum,
      imageLoaded,
      imageError,
      showRatingDelta,
      getRatingClass,
      getRatingDelta,
      getRatingDeltaClass,
      formatRelativeTime,
      formatAbsoluteDate,
      openAlbumDetails,
      closeAlbumModal,
      handleAlbumDeleted,
      imageLoadError
    }
  }
}
</script>

<style scoped>
@import '../../styles/tier-colors.css';

/* Main layout - vertical stacking for better alignment */
.changelog-entry-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Album info section (cover + artist/title) */
.album-info-section {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Album Cover Styles (AC#1, #6, #7) - Full row height */
.album-cover-container {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.album-cover-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.album-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.album-cover.image-loaded {
  opacity: 1;
}

.album-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.4);
}

.placeholder-icon {
  opacity: 0.5;
}

.album-cover-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  pointer-events: none;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Album text info button (artist + title) */
.album-text-info {
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  text-align: left;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.album-text-info:hover {
  background: rgba(255, 255, 255, 0.05);
}

.album-text-info:focus {
  outline: 2px solid rgba(78, 205, 196, 0.6);
  outline-offset: 2px;
}

.album-text-info:active {
  transform: translateY(1px);
}

.album-text-info .artist-name {
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.album-text-info:hover .artist-name {
  color: #f0f0f0;
}

.album-text-info .album-title {
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.album-text-info:hover .album-title {
  color: #f0f0f0;
}

/* Change details section */
.change-details-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.change-description {
  flex: 1;
}

.change-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.change-main-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #e8e8e8;
}

.reviewer-info {
  color: #ffa726;
  font-weight: 600;
}

.rating-values {
  display: flex;
  align-items: center;
  gap: 12px;
}

.arrow-icon {
  color: #bbb;
  flex-shrink: 0;
  margin: 0 4px;
  opacity: 0.8;
}

.old-rating,
.new-rating {
  padding: 6px 12px;
  border-radius: 8px;
  font-weight: 700;
  color: #000;
  display: inline-block;
  min-width: 50px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: default;
  position: relative;
  overflow: hidden;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.old-rating:hover,
.new-rating:hover {
  transform: translateY(-1px);
}


.rating-s { 
  background: var(--tier-s-color, #ff4500); 
  border: 1px solid var(--tier-s-border, rgba(255, 69, 0, 0.5));
  box-shadow: 0 0 8px var(--tier-s-glow, rgba(255, 69, 0, 0.4));
}
.rating-a { 
  background: var(--tier-a-color, #ff8c00); 
  border: 1px solid var(--tier-a-border, rgba(255, 140, 0, 0.5));
  box-shadow: 0 0 8px var(--tier-a-glow, rgba(255, 140, 0, 0.4));
}
.rating-b { 
  background: var(--tier-b-color, #ffd700); 
  border: 1px solid var(--tier-b-border, rgba(255, 215, 0, 0.5));
  box-shadow: 0 0 8px var(--tier-b-glow, rgba(255, 215, 0, 0.4));
}
.rating-c { 
  background: var(--tier-c-color, #32cd32); 
  border: 1px solid var(--tier-c-border, rgba(50, 205, 50, 0.5));
  box-shadow: 0 0 8px var(--tier-c-glow, rgba(50, 205, 50, 0.4));
}
.rating-d { 
  background: var(--tier-d-color, #00bfff); 
  border: 1px solid var(--tier-d-border, rgba(0, 191, 255, 0.5));
  box-shadow: 0 0 8px var(--tier-d-glow, rgba(0, 191, 255, 0.4));
}
.rating-e { 
  background: var(--tier-e-color, #708090); 
  border: 1px solid var(--tier-e-border, rgba(112, 128, 144, 0.5));
  box-shadow: 0 0 8px var(--tier-e-glow, rgba(112, 128, 144, 0.4));
}

.rating-now-listening {
  background: var(--tier-now-listening-color, #9370db);
  border: 1px solid var(--tier-now-listening-border, rgba(147, 112, 219, 0.5));
  box-shadow: 0 0 8px var(--tier-now-listening-glow, rgba(147, 112, 219, 0.4));
  color: #ffffff;
}

.rating-not-rated {
  background: var(--tier-not-rated-color, #a8a8a8);
  border: 1px solid var(--tier-not-rated-border, rgba(168, 168, 168, 0.5));
  box-shadow: 0 0 8px var(--tier-not-rated-glow, rgba(168, 168, 168, 0.4));
  color: #000000;
  opacity: 0.8;
}

.rating-tiering-herrie {
  background: linear-gradient(135deg, #5a6d7a 0%, #7a8a99 100%);
  border: 1px solid rgba(90, 109, 122, 0.6);
  box-shadow: 0 0 12px rgba(122, 138, 153, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

.rating-tiering-herrie::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: icy-shimmer 3s ease-in-out infinite;
}

@keyframes icy-shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.skull-icon {
  margin-right: 4px;
  vertical-align: middle;
  opacity: 0.9;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}

.change-metadata {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #bbb;
  margin-top: auto;
}

.metadata-icon {
  opacity: 0.7;
  color: #bbb;
  flex-shrink: 0;
}

/* Responsive design - Mobile optimization (AC#5) */
@media (max-width: 640px) {
  .changelog-entry-content {
    gap: 12px;
  }

  .album-info-section {
    gap: 10px;
    padding-bottom: 12px;
  }

  .album-cover-container {
    width: 70px;
    height: 70px;
  }

  .album-text-info {
    padding: 4px 8px;
    /* Ensure 44px touch target */
    min-height: 44px;
    justify-content: center;
  }

  .album-text-info .artist-name {
    font-size: 13px;
  }

  .album-text-info .album-title {
    font-size: 14px;
  }

  .change-main-text {
    font-size: 14px;
  }

  .rating-values {
    flex-wrap: wrap;
    gap: 8px;
  }

  .old-rating,
  .new-rating {
    font-size: 13px;
    padding: 6px 10px;
    min-width: 45px;
  }

  .arrow-icon {
    margin: 0 2px;
  }

  .change-metadata {
    font-size: 12px;
    gap: 4px;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .old-rating,
  .new-rating {
    border: 2px solid #ffffff;
  }

  .album-text-info {
    border: 2px solid rgba(255, 255, 255, 0.2);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .album-text-info,
  .album-cover {
    transition: none;
  }

  .album-text-info:active {
    transform: none;
  }
}
</style>